import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import { permissionsApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Trash, CircleNotch, Key } from '@phosphor-icons/react';
import { formatApiErrorDetail } from '../lib/utils';
import { toast } from 'sonner';

const MODULES = ['products', 'qr', 'reports', 'admin', 'other'];

export default function PermissionsPage() {
  const { t } = useTranslation();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    module: 'products',
  });

  const loadPermissions = useCallback(async () => {
    try {
      const response = await permissionsApi.list();
      setPermissions(response.data);
    } catch (error) {
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const handleOpenDialog = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      module: 'products',
    });
    setDialogOpen(true);
  };

  const slugifyPermissionCode = (name, module) => {
    const base = (name || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    const mod = (module || 'other').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
    return base ? `${mod}.${base}` : `${mod}.permission`;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        code: formData.code?.trim() || slugifyPermissionCode(formData.name, formData.module),
      };
      await permissionsApi.create(payload);
      toast.success('Permission created successfully');
      setDialogOpen(false);
      loadPermissions();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this permission?')) return;
    try {
      await permissionsApi.delete(id);
      toast.success('Permission deleted successfully');
      loadPermissions();
    } catch (error) {
      toast.error('Failed to delete permission');
    }
  };

  // Group permissions by module
  const groupedPermissions = permissions.reduce((acc, perm) => {
    const module = perm.module || 'other';
    if (!acc[module]) acc[module] = [];
    acc[module].push(perm);
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="permissions-page">
        <PageHeader
          title={t('permissions.title')}
          description={t('permissions.subtitle')}
          actions={(
            <Button onClick={handleOpenDialog} data-testid="add-permission-btn">
              <Plus className="mr-2 h-4 w-4" />
              {t('permissions.addPermission')}
            </Button>
          )}
        />

        {/* Permissions by Module */}
        {loading ? (
          <Card className="bg-white border-zinc-200 shadow-sm">
            <CardContent className="py-8 text-center text-zinc-400">
              <CircleNotch className="w-6 h-6 animate-spin mx-auto" />
            </CardContent>
          </Card>
        ) : (
          Object.entries(groupedPermissions).map(([module, perms]) => (
            <Card key={module} className="bg-white border-zinc-200 shadow-sm">
              <CardContent className="p-0">
                <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
                  <h3 className="text-sm font-semibold text-zinc-900 uppercase tracking-wider flex items-center gap-2">
                    <Key className="w-4 h-4 text-brand-600" />
                    {module}
                  </h3>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-200 hover:bg-transparent">
                      <TableHead className="text-zinc-500">{t('common.name')}</TableHead>
                      <TableHead className="text-zinc-500">{t('common.description')}</TableHead>
                      <TableHead className="text-zinc-500 text-right">{t('common.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {perms.map((perm) => (
                      <TableRow key={perm.id} className="border-zinc-200 hover:bg-zinc-50" data-testid={`perm-row-${perm.id}`}>
                        <TableCell className="text-zinc-900 font-medium">{perm.name}</TableCell>
                        <TableCell className="text-zinc-500">{perm.description || '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(perm.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-perm-${perm.id}`}
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))
        )}

        {/* Add Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white border-zinc-200 text-zinc-900 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-zinc-900 font-heading">{t('permissions.addPermission')}</DialogTitle>
              <DialogDescription className="text-zinc-500">
                {t('permissions.addDesc')}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-700">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="View Products"
                  className="bg-white border-zinc-300 text-zinc-900"
                  data-testid="perm-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-700">{t('permissions.module')} *</Label>
                <Select
                  value={formData.module}
                  onValueChange={(v) => setFormData({ ...formData, module: v })}
                >
                  <SelectTrigger className="bg-white border-zinc-300 text-zinc-900" data-testid="perm-module-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-zinc-200">
                    {MODULES.map((mod) => (
                      <SelectItem key={mod} value={mod} className="text-zinc-900 focus:bg-zinc-100">
                        {mod.charAt(0).toUpperCase() + mod.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-700">{t('common.description')}</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-white border-zinc-300 text-zinc-900"
                  data-testid="perm-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-zinc-300 text-zinc-700 hover:bg-zinc-100"
                data-testid="cancel-perm-btn"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !formData.name}
                className="bg-brand-600 hover:bg-brand-700 text-white"
                data-testid="save-perm-btn"
              >
                {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
